/**
 * "Scheduled email" on the Reports page (Operator Reports (b), migration 0257): the operator
 * admin's weekly or monthly report, emailed to a list of addresses. Each schedule sends the
 * period's tickets spreadsheet (CSV or Excel), the PDF statement (operator Reports (c), 0256), or
 * both, from 06:00 on its send day in its zone.
 *
 * OPERATOR_ADMIN only: Reports.tsx mounts this for the admin, and the API refuses anyone else.
 * The report is read as its owner, the admin who last saved it, so saving takes it over.
 * "Send now" emails the last closed period to the signed-in admin only.
 */

import React from 'react';
import { ApiError } from '@lodex-pro/web-shared';
import type {
  CreateOperatorReportScheduleRequest,
  OperatorReportFrequency,
  OperatorReportRunStatus,
  OperatorReportScheduleView,
  OperatorReportTicketFormat,
} from '@lodex-pro/shared/validation/operator-report-schedules';
import { usePortalVolumes } from '../../hooks/useOperatorPortal';
import {
  useAddReportSchedule,
  useRemoveReportSchedule,
  useReportSchedules,
  useSendReportNow,
  useUpdateReportSchedule,
} from '../../hooks/useReportSchedules';

const CARD: React.CSSProperties = {
  padding: '16px',
  background: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  marginBottom: '16px',
};
const LABEL: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  fontSize: '13px',
};
const CHECK: React.CSSProperties = {
  ...LABEL,
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
};
const INPUT: React.CSSProperties = {
  padding: '6px 8px',
  borderRadius: '10px',
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  fontSize: '13px',
};
const BUTTON: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  fontSize: '13px',
  cursor: 'pointer',
};
const MUTED: React.CSSProperties = { color: 'var(--color-text-tertiary)', fontSize: '13px' };
const ALERT: React.CSSProperties = {
  color: 'var(--color-danger)',
  fontSize: '13px',
  marginBottom: 0,
};

export const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export const RUN_STATUS_LABEL: Readonly<Record<OperatorReportRunStatus, string>> = {
  BUILDING: 'Being made',
  READY: 'Sending',
  SENT: 'Sent',
  PARTIAL: 'Sent, but not to every address',
  NO_TICKETS: 'No tickets that period, so nothing was sent',
  FAILED: 'Could not be made',
  SKIPPED_PLAN: 'Not sent: reports are not on your plan',
  SKIPPED_OWNER: 'Not sent: its owner can no longer sign in. Save it to take it over.',
};

export const OWNER_NOTE =
  'The report shows the tickets its owner can see. Saving it makes you the owner.';
export const STATEMENT_DOWN_NOTE =
  'Statements can’t be made right now. Until that’s fixed, a scheduled statement is left out ' +
  'and the email says so.';
export const SCHEDULE_FAILED_MESSAGE = 'Couldn’t save the schedule. Try again.';

const ZONES = [
  'America/Chicago',
  'America/Denver',
  'America/New_York',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
];

/** "Monthly on the 1st", "Weekly on Monday". */
export function cadenceText(s: {
  frequency: OperatorReportFrequency;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
}): string {
  if (s.frequency === 'WEEKLY') return `Weekly on ${WEEKDAYS[s.dayOfWeek ?? 1] ?? 'Monday'}`;
  const d = s.dayOfMonth ?? 1;
  const suffix =
    d % 10 === 1 && d !== 11
      ? 'st'
      : d % 10 === 2 && d !== 12
        ? 'nd'
        : d % 10 === 3 && d !== 13
          ? 'rd'
          : 'th';
  return `Monthly on the ${String(d)}${suffix}`;
}

/** "Tickets (CSV) and statement". */
export function contentsText(s: {
  ticketsFormat: OperatorReportTicketFormat | null;
  includeStatement: boolean;
  statementRunTickets: boolean;
}): string {
  const parts: string[] = [];
  if (s.ticketsFormat !== null)
    parts.push(`Tickets (${s.ticketsFormat === 'CSV' ? 'CSV' : 'Excel'})`);
  if (s.includeStatement) {
    parts.push(s.statementRunTickets ? 'statement with run tickets' : 'statement');
  }
  return parts.join(' and ');
}

/** Addresses from a box: one per line or separated by commas, blanks dropped. */
export function parseRecipients(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter((x) => x !== '');
}

export function scheduleErrorMessage(err: Error): string {
  if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
    const f = err.fields[0];
    if (f?.path === 'timezone') return 'Choose a time zone.';
    if (f?.path === 'schedules') return `You have the most schedules allowed (${f.reason}).`;
    if (f?.path.startsWith('recipients') === true) return 'Check the email addresses.';
    if (f?.path === 'ticketsFormat') return 'Choose what the report contains.';
    return 'Check the schedule and try again.';
  }
  if (err instanceof ApiError && err.code === 'FEATURE_NOT_ENABLED') {
    return 'Scheduled reports are not on your plan.';
  }
  if (err instanceof ApiError && err.code === 'RATE_LIMIT_EXCEEDED') {
    return 'You’ve sent a few already this hour. Try again later.';
  }
  if (err instanceof ApiError && err.code === 'OPERATOR_REPORT_TOO_LARGE') {
    return 'That period is too large to email. Narrow the schedule to one site.';
  }
  if (err instanceof ApiError && err.code === 'OPERATOR_STATEMENT_UNAVAILABLE') {
    return 'The statement can’t be made right now. Try again in a few minutes.';
  }
  return SCHEDULE_FAILED_MESSAGE;
}

interface Draft {
  name: string;
  frequency: OperatorReportFrequency;
  dayOfWeek: number;
  dayOfMonth: number;
  timezone: string;
  siteId: string;
  ticketsFormat: OperatorReportTicketFormat | '';
  includeStatement: boolean;
  statementRunTickets: boolean;
  recipients: string;
}

function draftOf(s: OperatorReportScheduleView | null, zone: string): Draft {
  return {
    name: s?.name ?? 'Monthly report',
    frequency: s?.frequency ?? 'MONTHLY',
    dayOfWeek: s?.dayOfWeek ?? 1,
    dayOfMonth: s?.dayOfMonth ?? 1,
    timezone: s?.timezone ?? zone,
    siteId: s?.siteId ?? '',
    ticketsFormat: s === null ? 'XLSX' : (s.ticketsFormat ?? ''),
    includeStatement: s?.includeStatement ?? false,
    statementRunTickets: s?.statementRunTickets ?? false,
    recipients: (s?.recipients ?? [])
      .filter((r) => !r.unsubscribed)
      .map((r) => r.email)
      .join('\n'),
  };
}

export function bodyOf(d: Draft): CreateOperatorReportScheduleRequest {
  return {
    name: d.name,
    frequency: d.frequency,
    dayOfWeek: d.frequency === 'WEEKLY' ? d.dayOfWeek : null,
    dayOfMonth: d.frequency === 'MONTHLY' ? d.dayOfMonth : null,
    timezone: d.timezone,
    siteId: d.siteId === '' ? null : d.siteId,
    ticketsFormat: d.ticketsFormat === '' ? null : d.ticketsFormat,
    includeStatement: d.includeStatement,
    statementRunTickets: d.includeStatement && d.statementRunTickets,
    recipients: parseRecipients(d.recipients),
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** The sites with tickets in the last 90 days (Volumes "by site"), as the statement's list. */
function useSiteOptions(): Array<{ key: string; label: string }> {
  const range = React.useMemo(() => {
    const now = Date.now();
    return { from: new Date(now - 90 * DAY_MS).toISOString(), to: new Date(now).toISOString() };
  }, []);
  const sites = usePortalVolumes({ groupBy: 'site', ...range });
  return (sites.data?.groups ?? [])
    .filter((g) => g.key !== '(none)')
    .map((g) => ({ key: g.key, label: g.label }));
}

function ScheduleForm({
  initial,
  maxRecipients,
  statementAvailable,
  saving,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: OperatorReportScheduleView | null;
  maxRecipients: number;
  statementAvailable: boolean;
  saving: boolean;
  error: Error | null;
  submitLabel: string;
  onSubmit: (body: CreateOperatorReportScheduleRequest) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [d, setD] = React.useState<Draft>(() => draftOf(initial, browserZone));
  const set = <K extends keyof Draft>(k: K, v: Draft[K]): void => setD((p) => ({ ...p, [k]: v }));
  const sites = useSiteOptions();
  const zones = [...new Set([d.timezone, browserZone, ...ZONES])];
  const nothing = d.ticketsFormat === '' && !d.includeStatement;
  const addresses = parseRecipients(d.recipients);

  return (
    <form
      aria-label={submitLabel}
      style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}
      onSubmit={(e): void => {
        e.preventDefault();
        onSubmit(bodyOf(d));
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
        <label style={LABEL}>
          Name
          <input
            style={INPUT}
            value={d.name}
            maxLength={80}
            required
            onChange={(e): void => set('name', e.target.value)}
          />
        </label>
        <label style={LABEL}>
          How often
          <select
            style={INPUT}
            value={d.frequency}
            onChange={(e): void => set('frequency', e.target.value as OperatorReportFrequency)}
          >
            <option value="MONTHLY">Monthly (last month)</option>
            <option value="WEEKLY">Weekly (the 7 days before)</option>
          </select>
        </label>
        {d.frequency === 'WEEKLY' ? (
          <label style={LABEL}>
            Day
            <select
              style={INPUT}
              value={String(d.dayOfWeek)}
              onChange={(e): void => set('dayOfWeek', Number(e.target.value))}
            >
              {WEEKDAYS.map((w, i) => (
                <option key={w} value={String(i)}>
                  {w}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label style={LABEL}>
            Day of the month
            <select
              style={INPUT}
              value={String(d.dayOfMonth)}
              onChange={(e): void => set('dayOfMonth', Number(e.target.value))}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={String(n)}>
                  {String(n)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label style={LABEL}>
          Time zone
          <select
            style={INPUT}
            value={d.timezone}
            onChange={(e): void => set('timezone', e.target.value)}
          >
            {zones.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </label>
        <label style={LABEL}>
          Site
          <select
            style={INPUT}
            value={d.siteId}
            onChange={(e): void => set('siteId', e.target.value)}
          >
            <option value="">All sites</option>
            {d.siteId !== '' && !sites.some((s) => s.key === d.siteId) && (
              <option value={d.siteId}>The saved site (no tickets lately)</option>
            )}
            {sites.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
        <label style={LABEL}>
          Tickets spreadsheet
          <select
            style={INPUT}
            value={d.ticketsFormat}
            onChange={(e): void =>
              set('ticketsFormat', e.target.value as OperatorReportTicketFormat | '')
            }
          >
            <option value="XLSX">Excel</option>
            <option value="CSV">CSV</option>
            <option value="">None</option>
          </select>
        </label>
        <label style={CHECK}>
          <input
            type="checkbox"
            checked={d.includeStatement}
            onChange={(e): void => set('includeStatement', e.target.checked)}
          />
          PDF statement
        </label>
        {d.includeStatement && (
          <label style={CHECK}>
            <input
              type="checkbox"
              checked={d.statementRunTickets}
              onChange={(e): void => set('statementRunTickets', e.target.checked)}
            />
            Include run tickets
          </label>
        )}
      </div>
      {d.includeStatement && !statementAvailable && <p style={MUTED}>{STATEMENT_DOWN_NOTE}</p>}
      <label style={LABEL}>
        Send to (one address per line, up to {String(maxRecipients)})
        <textarea
          style={{ ...INPUT, minHeight: '72px' }}
          value={d.recipients}
          onChange={(e): void => set('recipients', e.target.value)}
        />
      </label>
      <p style={{ ...MUTED, margin: 0 }}>{OWNER_NOTE}</p>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          type="submit"
          style={BUTTON}
          disabled={saving || nothing || addresses.length === 0 || addresses.length > maxRecipients}
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
        <button type="button" style={BUTTON} onClick={onCancel}>
          Cancel
        </button>
      </div>
      {nothing && (
        <p role="alert" style={ALERT}>
          Choose a spreadsheet, the statement, or both.
        </p>
      )}
      {error !== null && (
        <p role="alert" style={ALERT}>
          {scheduleErrorMessage(error)}
        </p>
      )}
    </form>
  );
}

function AddSchedule({
  maxRecipients,
  statementAvailable,
  onDone,
}: {
  maxRecipients: number;
  statementAvailable: boolean;
  onDone: () => void;
}): React.JSX.Element {
  const add = useAddReportSchedule();
  return (
    <ScheduleForm
      initial={null}
      maxRecipients={maxRecipients}
      statementAvailable={statementAvailable}
      saving={add.isPending}
      error={add.error}
      submitLabel="Add schedule"
      onSubmit={(body): void => add.mutate(body, { onSuccess: onDone })}
      onCancel={onDone}
    />
  );
}

function ScheduleItem({
  schedule,
  maxRecipients,
  statementAvailable,
}: {
  schedule: OperatorReportScheduleView;
  maxRecipients: number;
  statementAvailable: boolean;
}): React.JSX.Element {
  const update = useUpdateReportSchedule(schedule.id);
  const remove = useRemoveReportSchedule(schedule.id);
  const sendNow = useSendReportNow(schedule.id);
  const [editing, setEditing] = React.useState(false);
  const active = schedule.recipients.filter((r) => !r.unsubscribed).length;
  const unsubscribed = schedule.recipients.length - active;
  const last = schedule.lastRun;

  return (
    <li
      style={{
        borderTop: '1px solid var(--color-border-primary)',
        padding: '12px 0',
        listStyle: 'none',
      }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}
      >
        <div>
          <strong>{schedule.name}</strong>
          {!schedule.enabled && <span style={MUTED}> (paused)</span>}
          <div style={MUTED}>
            {cadenceText(schedule)} · {contentsText(schedule)} · {String(active)}{' '}
            {active === 1 ? 'address' : 'addresses'}
            {unsubscribed > 0 && ` (${String(unsubscribed)} unsubscribed)`}
          </div>
          <div style={MUTED}>
            {schedule.nextSendDay === null
              ? 'Not scheduled'
              : `Next: ${schedule.nextSendDay} from 6:00 AM (${schedule.timezone})`}
            {schedule.owner.name !== null && ` · Owner: ${schedule.owner.name}`}
          </div>
          {last !== null && (
            <div style={MUTED}>
              Last: {last.periodStart} to {last.periodEnd} — {RUN_STATUS_LABEL[last.status]}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <button type="button" style={BUTTON} onClick={(): void => setEditing((v) => !v)}>
            Edit
          </button>
          <button
            type="button"
            style={BUTTON}
            disabled={update.isPending}
            onClick={(): void => update.mutate({ enabled: !schedule.enabled })}
          >
            {schedule.enabled ? 'Pause' : 'Resume'}
          </button>
          <button
            type="button"
            style={BUTTON}
            disabled={sendNow.isPending}
            onClick={(): void => sendNow.mutate({})}
          >
            {sendNow.isPending ? 'Sending…' : 'Send me a copy now'}
          </button>
          <button
            type="button"
            style={BUTTON}
            disabled={remove.isPending}
            onClick={(): void => {
              if (window.confirm(`Remove “${schedule.name}”? Its history goes with it.`)) {
                remove.mutate({});
              }
            }}
          >
            Remove
          </button>
        </div>
      </div>
      {sendNow.isSuccess && (
        <p role="status" style={{ ...MUTED, marginBottom: 0 }}>
          {sendNow.data.status === 'SENT'
            ? `Sent ${sendNow.data.periodStart} to ${sendNow.data.periodEnd} to ${sendNow.data.to}.`
            : `No tickets from ${sendNow.data.periodStart} to ${sendNow.data.periodEnd}, so nothing was sent.`}
        </p>
      )}
      {(sendNow.error ?? update.error ?? remove.error) !== null && (
        <p role="alert" style={ALERT}>
          {scheduleErrorMessage((sendNow.error ?? update.error ?? remove.error)!)}
        </p>
      )}
      {editing && (
        <ScheduleForm
          initial={schedule}
          maxRecipients={maxRecipients}
          statementAvailable={statementAvailable}
          saving={update.isPending}
          error={update.error}
          submitLabel="Save"
          onSubmit={(body): void =>
            update.mutate(body, {
              onSuccess: () => setEditing(false),
            })
          }
          onCancel={(): void => setEditing(false)}
        />
      )}
    </li>
  );
}

export function ReportSchedulesCard(): React.JSX.Element {
  const list = useReportSchedules();
  const [adding, setAdding] = React.useState(false);
  const data = list.data;

  return (
    <section aria-label="Scheduled email" style={CARD}>
      <h2 style={{ fontSize: '16px', marginTop: 0, marginBottom: '4px' }}>Scheduled email</h2>
      <p style={{ ...MUTED, marginTop: 0 }}>
        Email last week’s or last month’s tickets and statement to your office, on the day you
        choose, from 6:00 AM.
      </p>
      {list.isLoading && <p role="status">Loading…</p>}
      {list.isError && (
        <p role="alert" style={ALERT}>
          Could not load the schedules.
        </p>
      )}
      {data !== undefined && !data.reportsEnabled && (
        <p style={MUTED}>Scheduled reports are not on your plan, so none are sent.</p>
      )}
      {data !== undefined && (
        <>
          {data.schedules.length > 0 && (
            <ul style={{ padding: 0, margin: 0 }}>
              {data.schedules.map((s) => (
                <ScheduleItem
                  key={s.id}
                  schedule={s}
                  maxRecipients={data.maxRecipients}
                  statementAvailable={data.statementAvailable}
                />
              ))}
            </ul>
          )}
          {adding ? (
            <AddSchedule
              maxRecipients={data.maxRecipients}
              statementAvailable={data.statementAvailable}
              onDone={(): void => setAdding(false)}
            />
          ) : (
            data.reportsEnabled &&
            data.schedules.length < data.maxSchedules && (
              <button
                type="button"
                style={{ ...BUTTON, marginTop: '12px' }}
                onClick={(): void => setAdding(true)}
              >
                Add a schedule
              </button>
            )
          )}
        </>
      )}
    </section>
  );
}
